import { useEffect, useRef, useState } from "react";
import quickBooksInvoicesApi from "../services/quickBooksInvoicesApi";
import { isInvoiceCreationBlocked, QuickBooksInvoiceApiError, QuickBooksInvoiceCapabilities } from "../types/quickBooksInvoices";
import type { QuickBooksInvoiceStatusHookResult } from "./useQuickBooksInvoiceStatus";

interface LegacyInvoiceData {
  request_id?: string;
  status?: string;
  can_retry?: boolean;
  message?: string;
  error?: string;
  solmaz_quickbooks_invoice_id?: string;
  invoice_number?: string | null;
}

interface Options {
  status: QuickBooksInvoiceStatusHookResult;
  showProgress: (message: string) => void;
  hideProgress: () => void;
  showError: (message: string) => void;
  legacyCreate: (shipmentId: number) => Promise<{ success: boolean; data?: LegacyInvoiceData }>;
  onLegacyCompleted: (data: LegacyInvoiceData) => void;
}

/** Queue acceptance and UI cancellation are separate from remote invoice completion. */
export function useSolmazInvoiceCreation(shipmentId: string | number, options: Options) {
  const [capabilities, setCapabilities] = useState<QuickBooksInvoiceCapabilities | null>(null);
  const [enqueueing, setEnqueueing] = useState(false);
  const enqueueController = useRef<AbortController | null>(null);
  const currentShipment = useRef(shipmentId);
  const callbacks = useRef(options);
  currentShipment.current = shipmentId;
  callbacks.current = options;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setCapabilities(null);
    setEnqueueing(false);
    void quickBooksInvoicesApi.getCapabilities(controller.signal)
      .then((value) => { if (active) setCapabilities(value); })
      .catch(() => { if (active) setCapabilities(null); });
    return () => {
      active = false;
      controller.abort();
      if (enqueueController.current) callbacks.current.hideProgress();
      enqueueController.current?.abort();
      enqueueController.current = null;
    };
  }, [shipmentId]);

  const create = async () => {
    if (currentShipment.current !== shipmentId || enqueueController.current || isInvoiceCreationBlocked(callbacks.current.status)) return;
    const controller = new AbortController();
    enqueueController.current = controller; // Synchronous same-tick click guard.
    setEnqueueing(true);
    options.showProgress("Queuing QuickBooks invoice...");
    const isCurrent = () => !controller.signal.aborted && currentShipment.current === shipmentId;
    let failed = false;
    let enqueueStarted = false;
    try {
      const available = capabilities ?? await quickBooksInvoicesApi.getCapabilities(controller.signal);
      if (!isCurrent() || isInvoiceCreationBlocked(callbacks.current.status)) return;
      const id = Number(shipmentId);
      if (available.enabled) {
        enqueueStarted = true;
        await quickBooksInvoicesApi.enqueue(id, controller.signal);
        if (isCurrent()) options.status.refresh();
      } else if (!callbacks.current.status.status && !isInvoiceCreationBlocked(callbacks.current.status)) {
        // Feature-off only, with no previous request. Never fall back after an error.
        const result = await options.legacyCreate(id);
        if (!isCurrent()) return;
        if (result.success && result.data?.request_id && result.data?.status) {
          // The flag may have changed after capabilities were read. The
          // legacy gate can safely return a queued operation in that race.
          options.status.refresh();
        } else if (!result.success || !result.data?.solmaz_quickbooks_invoice_id) {
          failed = true;
          options.showError(result.data?.error ?? result.data?.message ?? "Invoice response could not be confirmed. Refresh the shipment.");
        } else options.onLegacyCompleted(result.data);
      }
    } catch (caught) {
      const error = caught as QuickBooksInvoiceApiError;
      if (isCurrent() && !error?.isCanceled) {
        failed = true;
        options.showError(error?.message ?? "We couldn't queue the QuickBooks invoice");
        // A lost enqueue response may still have committed. Discover it via GET,
        // rather than assuming rejection or resubmitting through the legacy path.
        if (enqueueStarted && error?.status !== 401 && error?.status !== 403) options.status.refresh();
      }
    } finally {
      if (enqueueController.current === controller) {
        enqueueController.current = null;
        setEnqueueing(false);
        if (!failed) options.hideProgress();
      }
    }
  };

  return { enqueueing, create };
}
